import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Loader2, Mail, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("verifying"); // verifying, success, error, resend
  const [errorMessage, setErrorMessage] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus("resend");
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.post(`${API}/api/auth/verify-email?token=${token}`);
      
      if (response.data.success) {
        setStatus("success");
        
        // Auto-login the user
        if (response.data.access_token && response.data.user) {
          loginWithToken(response.data.access_token, response.data.user);
          toast.success("Email verified! Welcome to Financify.");
          
          // Redirect to dashboard after short delay
          setTimeout(() => {
            navigate("/dashboard");
          }, 2000);
        }
      }
    } catch (error) {
      setStatus("error");
      setErrorMessage(error.response?.data?.detail || "Verification failed");
    }
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) {
      toast.error("Please enter your email address");
      return;
    }

    setResending(true);
    try {
      await axios.post(`${API}/api/auth/resend-verification?email=${encodeURIComponent(resendEmail)}`);
      toast.success("If an account exists, a new verification link has been sent!");
      setResendEmail("");
    } catch (error) {
      toast.error("Failed to resend verification email");
    } finally {
      setResending(false);
    }
  };

  // Verifying state
  if (status === "verifying") {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="card-base text-center py-12">
            <Loader2 className="w-12 h-12 text-[#52BFB8] animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Your Email</h1>
            <p className="text-gray-600">Please wait while we verify your email address...</p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (status === "success") {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="card-base text-center py-10">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h1>
            <p className="text-gray-600 mb-6">
              Your email has been verified successfully. You're being redirected to your dashboard...
            </p>
            <div className="flex items-center justify-center gap-2 text-[#52BFB8]">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Redirecting...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (status === "error") {
    return (
      <div className="min-h-screen bg-gray-50/50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="card-base text-center py-10">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
            <p className="text-gray-600 mb-6">{errorMessage}</p>
            
            <div className="space-y-3">
              <Button 
                className="w-full btn-primary"
                onClick={() => setStatus("resend")}
              >
                <Mail className="w-4 h-4 mr-2" />
                Request New Verification Link
              </Button>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Resend state (no token or user requested)
  return (
    <div className="min-h-screen bg-gray-50/50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#52BFB8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-[#52BFB8]" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h1>
          <p className="text-gray-600">
            Enter your email address to receive a new verification link.
          </p>
        </div>

        <div className="card-base">
          <form onSubmit={handleResend} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="pl-10"
                  placeholder="your@email.com"
                  data-testid="resend-email-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary" 
              disabled={resending}
              data-testid="resend-verification-btn"
            >
              {resending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Send Verification Link
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center mb-4">
              Already verified?
            </p>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                Go to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
